import React from 'react'
import { Target, Coins, Clock, TrendingUp, UsersRound, Info } from 'lucide-react'
import { StableTooltip } from "@/components/ui/stable-tooltip"
import { IconTile } from "@/components/IconTile"
import { TIME_CONFIG } from "@/../../shared/constants/timeConfig"

interface StrategicSummaryProps {
  // Financial metrics
  annualSavings: number
  totalFTEs: number
  benchmarkSavings?: number  // NEW: Benchmark savings value
  
  // Time savings metrics
  fteCount?: number
  
  // Automation metrics
  validatedAutomation?: number
  clientWeightedAutomation?: number  // Legacy field for migration
  benchmarkAutomation: number
}

export default function StrategicSummary({
  annualSavings,
  totalFTEs,
  benchmarkSavings,
  validatedAutomation,
  benchmarkAutomation
}: StrategicSummaryProps) {
  // Migrate legacy clientWeightedAutomation to validatedAutomation
  const finalValidatedAutomation = validatedAutomation ?? 0
  
  // Calculate hours saved: total FTEs × weighted automation % × hours/FTE/year
  const calculatedHoursSaved = (
    totalFTEs * (finalValidatedAutomation / 100) * TIME_CONFIG.HOURS_PER_FTE_YEAR)
  const fteEquivalentReleased = calculatedHoursSaved / 2080
  

  // Log updated values for audit
  React.useEffect(() => {
    console.log('📊 StrategicSummary Rendered Values:', {
      timestamp: new Date().toISOString(),
      calculatedHoursSaved,
      finalValidatedAutomation,
      validatedAutomationSource: validatedAutomation ? 'validatedAutomation' : 'default'
    })
  }, [calculatedHoursSaved, finalValidatedAutomation, validatedAutomation])
  
  // Format currency values
  const formatCurrency = (value: number, showAnnual: boolean = false) => {
    const suffix = showAnnual ? '/yr' : ''
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M${suffix}`
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K${suffix}`
    }
    return `$${value.toLocaleString()}${suffix}`
  }



  
  // Metric card component with dark theme styling and icons
  const MetricCard = ({ 
    icon: Icon, 
    label, 
    value, 
    tooltip, 
    className = "" 
  }: { 
    icon: React.ComponentType<any>; 
    label: string; 
    value: string; 
    tooltip?: string; 
    className?: string 
  }) => (
    <StableTooltip content={tooltip || ""} side="top">
      <div 
        className={`metric-card cursor-help group ${className}`}
        data-testid="strategic-metric-card"
        style={{
          border: '2px solid rgba(20, 184, 166, 0.25)',
          background: 'rgba(51, 65, 85, 0.05)'
        }}
      >
        <div className="metric-card-header">
          <div className="metric-card-icon bg-slate-800 rounded-lg p-1.5">
            <IconTile icon={Icon} size={32} iconSize={16} color="text-slate-500" bg="transparent" />
          </div>
          <div className="metric-card-badge">
            <Info className="w-4 h-4 text-slate-500 group-hover:text-slate-400 lucide-icon-muted transition-colors duration-200" />
          </div>
        </div>
        
        <div className="metric-card-label">
          {label}
        </div>
        
        <div className="metric-card-value">
          {value}
        </div>
      </div>
    </StableTooltip>
  )
  
  return (
    <div 
      data-testid="strategic-summary"
      className="strategic-summary mb-8"
    >
      {/* Row 1: Financial highlights */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        <MetricCard 
          icon={Coins}
          label="Deliverable Savings" 
          value={formatCurrency(annualSavings, true)} 
          tooltip="Annual savings from your current automation delivery and FTE allocations"
        />
        <MetricCard 
          icon={Clock}
          label="Time Savings" 
          value={`${Math.round(calculatedHoursSaved / 1000)}k hrs/yr`} 
          tooltip="Annual hours saved through automation (222 FTEs × weighted automation % × 1,536 hours/FTE/year)"
        />
        <MetricCard 
          icon={TrendingUp}
          label="Benchmark Savings" 
          value={benchmarkSavings ? formatCurrency(benchmarkSavings) : "—"} 
          tooltip="Potential annual savings if industry benchmark automation levels were achieved across all activities"
        />
      </div>
      
      {/* Row 2: Automation profile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <MetricCard 
          icon={Target}
          label="Deliverable Automation %"
          value={`${finalValidatedAutomation.toFixed(1)}%`} 
          tooltip="Percentage of FTE automation that has been confirmed and validated for delivery (weighted by FTEs)"
        />
        <MetricCard 
          icon={TrendingUp}
          label="Benchmark Automation %" 
          value={`${benchmarkAutomation.toFixed(1)}%`} 
          tooltip="Industry benchmark automation percentage based on top-quartile performance in similar organizations"
        />
        <MetricCard
          icon={UsersRound}
          label="FTE Equivalent Released"
          value={Number.isFinite(fteEquivalentReleased) ? fteEquivalentReleased.toFixed(1) : "0.0"}
          tooltip="FTE Equivalent Released = Total Hours Saved ÷ 2,080 (52 weeks × 40 hours)"
        />
      </div>
    </div>
  )
}
